/**
 * Thin DOM helpers for building the run-list pane.
 *
 * These are small, framework-free element constructors used by render.ts so
 * the renderer stays readable and every node is built with stable class
 * tokens (never `Math.random()` — Gate 2.6b). They are deliberately trivial
 * and carry no business logic.
 */
import type { Conclusion } from '../types';
import { conclusionClass } from '../lib/parse';

/** Create an element with an optional class and text content. */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** Create a pill span with the given class token and label. */
export function pill(className: string, label: string): HTMLSpanElement {
  return el('span', className, label);
}

/** Truncate a long hex id to a short display form (8 chars). */
export function truncateId(id: string): string {
  if (id.length <= 8) return id;
  return id.slice(0, 8);
}

/** Glyph for a concluded run's conclusion icon. */
const ICON_GLYPH: Record<string, string> = {
  ok: '✓',
  fail: '✗',
  warn: '!',
  skip: '–',
  neutral: '·',
};

/** Build a conclusion icon span (glyph + class token) for a concluded run. */
export function conclusionIcon(conclusion: Conclusion | undefined): HTMLSpanElement {
  const cls = conclusionClass(conclusion);
  const icon = el('span', `conclusion-icon conclusion-${cls}`, ICON_GLYPH[cls] ?? '·');
  icon.setAttribute('aria-hidden', 'true');
  return icon;
}
